'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    ALLERGY_SEVERITY_COLORS,
    ALLERGY_SEVERITY_LABELS,
    ALLERGY_STATUS_LABELS,
    ALLERGY_TYPE_LABELS,
    Allergy,
    AllergySeverity,
    AllergyStatus,
    AllergyType,
    CreateAllergyData,
    UpdateAllergyData,
    createAllergy,
    deleteAllergy,
    getAllergiesByClient,
    updateAllergy,
} from '@/lib/api/prontuario/prontuario-api'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useEffect, useState } from 'react'
import {
    MdAdd,
    MdCheck,
    MdClose,
    MdDelete,
    MdEdit,
    MdWarning,
} from 'react-icons/md'
import { toast } from 'react-toastify'
import { useAuth } from '@/store/hooks'
import { ProfessionalSearchSelector } from '@/components/ProfessionalSearchSelector'
import { useProntuarioData } from './prontuario-context'

interface AllergyFormData {
    professionalId: string
    type: AllergyType
    allergen: string
    severity: AllergySeverity
    reaction: string
    onsetDate: string
    status: AllergyStatus
    notes: string
}

const defaultFormData: AllergyFormData = {
    professionalId: '',
    type: AllergyType.MEDICATION,
    allergen: '',
    severity: AllergySeverity.MILD,
    reaction: '',
    onsetDate: '',
    status: AllergyStatus.ACTIVE,
    notes: '',
}

export default function AlergiasPage() {
    const { user, isProfessional } = useAuth()
    const { client, reloadAllergies, clientId } = useProntuarioData()

    const [allergies, setAllergies] = useState<Allergy[]>([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [modalOpen, setModalOpen] = useState(false)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [formData, setFormData] = useState<AllergyFormData>(defaultFormData)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [allergyToDelete, setAllergyToDelete] = useState<number | null>(null)

    useEffect(() => {
        if (clientId) {
            loadAllergies()
        }
    }, [clientId])

    const loadAllergies = async () => {
        if (!clientId) return

        setLoading(true)
        try {
            const response = await getAllergiesByClient(parseInt(clientId, 10))
            if (response.success) {
                setAllergies(response.result || [])
            } else {
                toast.error(response.error || 'Erro ao carregar alergias')
            }
        } catch (error) {
            toast.error('Erro ao carregar alergias')
        } finally {
            setLoading(false)
        }
    }

    const openModal = (allergy?: Allergy) => {
        if (allergy) {
            setEditingId(allergy.id)
            setFormData({
                professionalId: allergy.registeredById ? String(allergy.registeredById) : '',
                type: allergy.type,
                allergen: allergy.allergen,
                severity: allergy.severity,
                reaction: allergy.reaction || '',
                onsetDate: allergy.onsetDate ? allergy.onsetDate.split('T')[0] : '',
                status: allergy.status,
                notes: allergy.notes || '',
            })
        } else {
            setEditingId(null)
            setFormData(defaultFormData)
        }
        setModalOpen(true)
    }

    const closeModal = () => {
        setModalOpen(false)
        setEditingId(null)
        setFormData(defaultFormData)
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!isProfessional && !formData.professionalId) {
            toast.error('Selecione um profissional responsável')
            return
        }

        if (!formData.allergen.trim()) {
            toast.error('Informe o alérgeno')
            return
        }

        if (!clientId || !user?.companyId) return

        setSaving(true)
        try {
            if (editingId) {
                const updateData: UpdateAllergyData = {
                    type: formData.type,
                    allergen: formData.allergen.trim(),
                    severity: formData.severity,
                    reaction: formData.reaction.trim() || undefined,
                    onsetDate: formData.onsetDate || undefined,
                    status: formData.status,
                    notes: formData.notes.trim() || undefined,
                }

                const response = await updateAllergy(editingId, updateData)
                if (response.success) {
                    toast.success('Alergia atualizada com sucesso!')
                    await loadAllergies()
                    await reloadAllergies()
                    closeModal()
                } else {
                    toast.error(response.error || 'Erro ao atualizar alergia')
                }
            } else {
                const createData: CreateAllergyData = {
                    clientId: parseInt(clientId, 10),
                    companyId: user!.companyId!,
                    registeredById: isProfessional ? user!.professionalId! : parseInt(formData.professionalId, 10),
                    type: formData.type,
                    allergen: formData.allergen.trim(),
                    severity: formData.severity,
                    reaction: formData.reaction.trim() || undefined,
                    onsetDate: formData.onsetDate || undefined,
                    status: formData.status,
                    notes: formData.notes.trim() || undefined,
                }

                const response = await createAllergy(createData)
                if (response.success) {
                    toast.success('Alergia registrada com sucesso!')
                    await loadAllergies()
                    await reloadAllergies()
                    closeModal()
                } else {
                    toast.error(response.error || 'Erro ao registrar alergia')
                }
            }
        } catch (error) {
            toast.error('Erro ao salvar alergia')
        } finally {
            setSaving(false)
        }
    }

    const handleDelete = (id: number) => {
        setAllergyToDelete(id)
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!allergyToDelete) return

        try {
            const response = await deleteAllergy(allergyToDelete)
            if (response.success) {
                toast.success('Alergia excluída com sucesso!')
                await loadAllergies()
                await reloadAllergies()
            } else {
                toast.error(response.error || 'Erro ao excluir alergia')
            }
        } catch (error) {
            toast.error('Erro ao excluir alergia')
        }
    }

    const updateFormField = (field: keyof AllergyFormData, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }))
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[300px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">Carregando alergias...</p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-bold text-base-content">Alergias</h2>
                    <p className="text-sm text-base-content/60">
                        Gerencie as alergias do paciente
                    </p>
                </div>
                <button onClick={() => openModal()} className="btn btn-secondary gap-2">
                    <MdAdd className="text-lg" />
                    Nova Alergia
                </button>
            </div>

            {/* Allergies List */}
            {allergies.length === 0 ? (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-8 text-center">
                    <MdWarning className="text-4xl mx-auto mb-3 text-base-content/20" />
                    <p className="text-base-content/60">Nenhuma alergia registrada</p>
                    <p className="text-sm text-base-content/40 mt-1">
                        Clique no botão acima para registrar uma alergia
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {allergies.map(allergy => (
                        <div
                            key={allergy.id}
                            className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-sm overflow-hidden"
                        >
                            <div className="p-5">
                                <div className="flex items-start justify-between mb-3">
                                    <div className="flex items-center gap-3">
                                        <div className={`p-2 rounded-lg ${allergy.status === AllergyStatus.ACTIVE
                                            ? 'bg-error/10'
                                            : 'bg-base-200'
                                            }`}>
                                            <MdWarning className={`text-xl ${allergy.status === AllergyStatus.ACTIVE
                                                ? 'text-error'
                                                : 'text-base-content/50'
                                                }`} />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-base-content">
                                                {allergy.allergen}
                                            </h3>
                                            <span className="text-sm text-base-content/60">
                                                {ALLERGY_TYPE_LABELS[allergy.type]}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <button
                                            onClick={() => openModal(allergy)}
                                            className="btn btn-ghost btn-sm btn-square"
                                        >
                                            <MdEdit className="text-lg" />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(allergy.id)}
                                            className="btn btn-ghost btn-sm btn-square text-error"
                                        >
                                            <MdDelete className="text-lg" />
                                        </button>
                                    </div>
                                </div>

                                <div className="flex flex-wrap gap-2 mb-3">
                                    <span className={`badge ${ALLERGY_SEVERITY_COLORS[allergy.severity]} text-white`}>
                                        {ALLERGY_SEVERITY_LABELS[allergy.severity]}
                                    </span>
                                    <span className={`badge ${allergy.status === AllergyStatus.ACTIVE
                                        ? 'badge-error'
                                        : 'badge-ghost'
                                        }`}>
                                        {ALLERGY_STATUS_LABELS[allergy.status]}
                                    </span>
                                </div>

                                {allergy.reaction && (
                                    <div className="mb-3">
                                        <p className="text-xs text-base-content/60 mb-1">Reação:</p>
                                        <p className="text-sm text-base-content">{allergy.reaction}</p>
                                    </div>
                                )}

                                {allergy.notes && (
                                    <div className="mb-3">
                                        <p className="text-xs text-base-content/60 mb-1">Observações:</p>
                                        <p className="text-sm text-base-content line-clamp-2">{allergy.notes}</p>
                                    </div>
                                )}

                                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-base-content/50 pt-3 border-t border-base-300/50">
                                    {allergy.registeredBy && (
                                        <span>Por: {allergy.registeredBy.name}</span>
                                    )}
                                    {allergy.onsetDate && (
                                        <span>
                                            Início: {format(new Date(allergy.onsetDate), 'dd/MM/yyyy', { locale: ptBR })}
                                        </span>
                                    )}
                                    <span>
                                        Registrado: {format(new Date(allergy.createdAt), 'dd/MM/yyyy', { locale: ptBR })}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal */}
            {modalOpen && (
                <div className="modal modal-open">
                    <div className="modal-box !max-w-2xl">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-bold">
                                {editingId ? 'Editar Alergia' : 'Nova Alergia'}
                            </h3>
                            <button
                                onClick={closeModal}
                                className="btn btn-ghost btn-sm btn-square"
                            >
                                <MdClose className="text-xl" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            {!isProfessional && (
                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">Profissional Responsável <span className="text-error">*</span></span>
                                    </label>
                                    <ProfessionalSearchSelector
                                        value={formData.professionalId ? Number(formData.professionalId) : null}
                                        onChange={(id) => updateFormField('professionalId', id ? String(id) : '')}
                                        label=""
                                        placeholder="Selecione..."
                                        disabled={!!editingId}
                                    />
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Tipo <span className="text-error">*</span>
                                        </span>
                                    </label>
                                    <select
                                        className="select select-bordered w-full"
                                        value={formData.type}
                                        onChange={e => updateFormField('type', e.target.value)}
                                    >
                                        {Object.entries(ALLERGY_TYPE_LABELS).map(([key, label]) => (
                                            <option key={key} value={key}>
                                                {label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Alergênico <span className="text-error">*</span>
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        className="input input-bordered w-full"
                                        placeholder="Ex: Penicilina, Amendoim..."
                                        value={formData.allergen}
                                        onChange={e => updateFormField('allergen', e.target.value)}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Gravidade <span className="text-error">*</span>
                                        </span>
                                    </label>
                                    <select
                                        className="select select-bordered w-full"
                                        value={formData.severity}
                                        onChange={e => updateFormField('severity', e.target.value)}
                                    >
                                        {Object.entries(ALLERGY_SEVERITY_LABELS).map(([key, label]) => (
                                            <option key={key} value={key}>
                                                {label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">Status</span>
                                    </label>
                                    <select
                                        className="select select-bordered w-full"
                                        value={formData.status}
                                        onChange={e => updateFormField('status', e.target.value)}
                                    >
                                        {Object.entries(ALLERGY_STATUS_LABELS).map(([key, label]) => (
                                            <option key={key} value={key}>
                                                {label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">Reação</span>
                                </label>
                                <textarea
                                    className="textarea textarea-bordered w-full"
                                    placeholder="Descreva a reação alergênica..."
                                    rows={3}
                                    value={formData.reaction}
                                    onChange={e => updateFormField('reaction', e.target.value)}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">Data de Início</span>
                                </label>
                                <input
                                    type="date"
                                    className="input input-bordered w-full"
                                    value={formData.onsetDate}
                                    onChange={e => updateFormField('onsetDate', e.target.value)}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">Observações</span>
                                </label>
                                <textarea
                                    className="textarea textarea-bordered w-full"
                                    placeholder="Observações adicionais..."
                                    rows={3}
                                    value={formData.notes}
                                    onChange={e => updateFormField('notes', e.target.value)}
                                />
                            </div>

                            <div className="modal-action">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="btn btn-ghost"
                                    disabled={saving}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-secondary gap-2"
                                    disabled={saving}
                                >
                                    {saving ? (
                                        <>
                                            <span className="loading loading-spinner loading-sm"></span>
                                            Salvando...
                                        </>
                                    ) : (
                                        <>
                                            <MdCheck className="text-lg" />
                                            Salvar
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                    <div className="modal-backdrop" onClick={closeModal}></div>
                </div>
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setAllergyToDelete(null)
                }}
                onConfirm={confirmDelete}
                title="Excluir alergia"
                message="Tem certeza que deseja excluir esta alergia?"
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
            />
        </div>
    )
}
